import { QuadtreeItem } from 'quadtree-lib';

import Game from '../../game';
import Element, { ElementParams } from '../element';
import LevelScene from '../../scene/LevelScene';
import Behaviour from '../../behaviour';
import Equipment from './equipment';
import Weapon, { WeaponParams } from './weapon';
import MissileLauncher, { MissileLauncherParams } from './missileLauncher';
import Reactor, { ReactorParams } from './reactor';
import Shield, { ShieldParams } from './shield';
import Generator, { GeneratorParams } from './generator';
import AttackOrder from '../../order/attackOrder';
import StrafeToOrder from '../../order/strafeToOrder';
import Animation, { AnimationParams } from '../animation';
import Resource from '../resource/resource';
import CapitalShip from './capitalShip';
import Particle from '../particle';

import { getAngleBy2XY, getCorrectAngle, getDiffBetween2Angles, getDistanceBy2XY, getReverseAngle, getXByDistanceAndAngle, getXYByDistanceAndAngle, getYByDistanceAndAngle } from '../../../utils/geometry';
import drawLifeBar from '../../../utils/lifeBar';
import { Faction } from '../../../utils/faction';
import { avg } from '../../../utils/math';

import { paths } from '../../../config/paths.config';
import * as spritesConfig from '../../../config/sprites.config'


enum ShipSection {
  FRONT = 'front',
  REAR = 'rear',
  SIDE = 'side',
};

const sectionHitMultiplier = {
  [ShipSection.FRONT]: 1,
  [ShipSection.SIDE]: 1.5,
  [ShipSection.REAR]: 2,
};

export interface ShipParams extends ElementParams {
  HP: number;

  accelerationSpeed?: number;
  decelerationSpeed?: number;
  brakeSpeed?: number;
  strafeSpeed?: number;
  canStrafe?: boolean
  weaponParamsArray?: Array<WeaponParams | MissileLauncherParams>;
  reactorParamsArray?: Array<ReactorParams>;
  shieldParams?: ShieldParams;
  generatorParams?: GeneratorParams;
  tractorRange?: number;
  built?: boolean;
  parentShip?: CapitalShip
  canJump?: boolean
}

export default class Ship extends Element implements ShipParams {
  scene!: LevelScene;
  HP: number = 10;
  accelerationSpeed: number = 0; // unit: px / second 
  decelerationSpeed: number = 0; // unit: px / second 
  brakeSpeed: number = 0;
  strafeSpeed: number = 0;
  canStrafe = true;
  weaponParamsArray: Array<WeaponParams | MissileLauncherParams> = [];
  reactorParamsArray: Array<ReactorParams> = [];
  shieldParams?: ShieldParams;
  generatorParams?: GeneratorParams;
  tractorRange!: number;
  built?: boolean;
  parentShip?: CapitalShip;
  canJump = false;

  selectable = true; // override default value
  HPMax: number;
  moveSpeedMax: number;
  weapons: Array<Weapon | MissileLauncher> = [];
  reactors: Array<Reactor> = [];
  shield?: Shield;
  generator!: Generator;

  jumpCharging = false;
  jumpCharge = 0;
  jumpChargeMax = 100;
  jumpChargePerSecond = 10;

  get equipments(): Array<Equipment> {
    return [
      ...this.weapons,
      ...(this.shield ? [this.shield] : []),
      ...(this.generator ? [this.generator] : []),
    ];
  }

  constructor(params: ShipParams) {
    super(params);
    this.applyParams(params);

    this.HPMax = this.HP;

    /* set moveSpeed threshold and reset movespeed */
    this.moveSpeedMax = this.moveSpeed;
    this.moveSpeed = 0;

    this.behaviour = new Behaviour({
      element: this,
    }); // add behaviour object

    /* auto-settings if not set */
    if (!this.decelerationSpeed)
      this.decelerationSpeed = this.accelerationSpeed * 1.5;
    if (!this.brakeSpeed)
      this.brakeSpeed = this.accelerationSpeed * 3;
    if (!this.strafeSpeed)
      this.strafeSpeed = this.accelerationSpeed / 2;

    /* Auto-equip */
    this.weaponParamsArray.forEach(params => {
      const isMissileLauncher = params.projectileParams && 'explosionRadius' in params.projectileParams;
      const weaponInstance = isMissileLauncher ? MissileLauncher : Weapon;
      new weaponInstance({
        ...params,
        ship: this,

      });
    });
    this.reactorParamsArray.forEach(params => {
      new Reactor({
        ...params,
        ship: this,

      });
    });
    if (this.shieldParams) {
      new Shield({
        ship: this,
        ...this.shieldParams
      });
    }
    if (this.generatorParams) {
      new Generator({
        ship: this,
        ...this.generatorParams
      });
    }
  }

  update(modifier: number): Ship {
    if (this?.building?.built === false) {
      this.build(modifier);
      return this;
    }

    if (this.isManuallyControlled())
      this.manualControls.pilot(this, modifier);
    else if (this.behaviour && this.behaviour.orders.length > 0)
      this.behaviour.update(modifier);
    else
      this.attackNearestEnemy();

    this.move(modifier);
    this.moveByExternalVector(modifier);

    this.equipments.forEach(equipment => equipment?.update(modifier));

    this.tractResources(modifier);
    this.clearWarFog();
    this.updateQuadtree();
    this.handleCollision();

    this.chargeJump(modifier, !this.jumpCharging);

    return this;
  }

  stop(): void {
    super.stop();
    this?.weapons.forEach((w) => {
      w.prioritytarget = undefined;
    });
  }

  takeDamages(amount: number): void {
    if (this.shield)
      amount = this.shield.takeDamages(amount);

    this.HP -= amount;
    if (this.HP <= 0)
      this.destroy();
  }

  heal(amount: number): void {
    this.HP = Math.max(Math.min(this.HP + amount, this.HPMax), 0);
  }

  get maxFiringRange(): number {
    return this.weapons.reduce((acc, cur) => Math.max(acc, cur.range), 0);
  }

  getNearestEnemy(): Element | undefined {
    return this.getNearestElement(
      this.getElementsInRange(
        this.getTargetsQuadtree({ includeHazards: false }),
        this.maxFiringRange
      )
    );
  }

  attackNearestEnemy(): void {
    if (this.isInWarFog) // don't bother is unit is currently conceiled in warfog
      return;

    const nearestEnemy = this.getNearestEnemy();
    if (nearestEnemy) {
      this.behaviour?.unshiftOrder(
        new AttackOrder({
          target: nearestEnemy,
          softMode: true,
        })
      );
    }
  }

  getCollisionSide(target: Ship): ShipSection {
    const diffAngle = getDiffBetween2Angles(this.moveAngle, target.lookAngle)
    if (diffAngle < 60)
      return ShipSection.REAR;
    else if (diffAngle >= 60 && diffAngle < 140)
      return ShipSection.SIDE;
    else
      return ShipSection.FRONT;
  }

  handleCollision(): void {
    const targetInCollision = this.getTargetsInCollision()[0] as Ship;
    if (!targetInCollision)
      return;
    /* "pushed" ships cannot collide again */
    if (this.externalVector.duration > 0 || targetInCollision.externalVector.duration > 0)
      return;

    /* (basic) crash damage based on size and speed */
    const mySpeed = this.isStrafing ? this.strafeSpeed : this.moveSpeed;
    const targetSpeed = targetInCollision.isStrafing ? targetInCollision.strafeSpeed : targetInCollision.moveSpeed;
    const mySizeAvg = avg([this.width, this.height]);
    const targetSizeAvg = avg([targetInCollision.width, targetInCollision.height]);
    const maxSpeed = Math.max(mySpeed, targetSpeed);

    /* Calculate angle between ships */
    const angleBetweenMeAndTarget = getAngleBy2XY({
      x1: this.x,
      y1: this.y,
      x2: targetInCollision.x,
      y2: targetInCollision.y,
    });

    /* Apply damage multipliers based on hit direction */
    const enemyAppliedMultiplier = sectionHitMultiplier[this.getCollisionSide(targetInCollision)];
    const myAppliedMultiplier = sectionHitMultiplier[targetInCollision.getCollisionSide(this)];

    /* Calculate damages with multipliers */
    const enemyInflictedDamages = (targetSizeAvg / 1000 * targetSpeed) * enemyAppliedMultiplier;
    this.takeDamages(enemyInflictedDamages);
    const myInflictedDamages = (mySizeAvg / 1000 * mySpeed) * myAppliedMultiplier;
    targetInCollision.takeDamages(myInflictedDamages);

    const avgSpeed = avg([mySpeed, targetSpeed]);
    const duration = 500;
    const deceleration = avgSpeed / duration * 1000; // smooth deceleration

    /* enemy bounce */
    targetInCollision.externalVector = {
      ...targetInCollision.externalVector,
      speed: Math.min(avgSpeed * mySizeAvg / targetSizeAvg * enemyAppliedMultiplier, maxSpeed),
      angle: angleBetweenMeAndTarget,
      duration,
      deceleration,
    };
    targetInCollision.moveSpeed = 0;

    /* my bounce */
    this.externalVector = {
      ...this.externalVector,
      speed: Math.min(avgSpeed * targetSizeAvg / mySizeAvg * myAppliedMultiplier, maxSpeed),
      angle: getReverseAngle(angleBetweenMeAndTarget),
      duration,
      deceleration,
    };
    this.moveSpeed = 0;
  }


  destroy(): void {
    new Animation({
      scene: this.scene,
      context: this.context,
      x: this.x,
      y: this.y,
      width: this.sprite?.width * 2,
      height: this.sprite?.height * 2,
      lookAngle: this.lookAngle,
      spriteParams: {
        src: `${paths.img.animations}/boom3.png`,
        spriteWidth: 780,
        spriteHeight: 910,
        numberOfFramesBeforeNextStep: 3,
        frameCount: 6,
        stageCount: 7,
      }
    } as AnimationParams).create();
    this.remove();
  }

  draw(): CanvasRenderingContext2D {
    if (this.isInWarFog)
      return this.context;


    const jumpChargeRatio = this.jumpCharge / this.jumpChargeMax;
    if (Math.random() >= (1 - jumpChargeRatio) && this.jumpCharging) {

      /* blink once */
      if (!this.blinkCtx) {
        this.buildBlinkFrame({ R: 255, G: 255, B: 255, A: 0.8 });
      }
      this.sprite.image = this.blinkCanvas;
      super.draw();
      this.setSprite();

    } else {
      super.draw();
    }

    this.drawJumpChargeBar();
    return this.context;
  }

  subDraw(): CanvasRenderingContext2D {
    if (this.building && !this.building.built) {
      this.drawConstructionBar();
    }

    this.equipments.forEach(equipment => equipment?.draw());
    this.drawLifeBar();
    this.drawControlGroupNumber();
    return this.context;
  }

  clearWarFog(): void {
    if (!('warFog' in this.scene)) return;
    if (!this.scene.warFog || this.faction !== Faction.PLAYER)
      return;

    const { x, y } = this;
    const radius =
      this.maxFiringRange * 1.5 // max weapon range if any equipped
      || (this.width + this.height) * 5 // ... or widh/height based value
      ;

    this.scene.warFog.clearArc({
      x,
      y,
      radius,
      startAngle: 0,
      endAngle: 2 * Math.PI,
    });

    /* set the "isInWarfog" attribute to false for elements in detection range */
    this.getElementsInRange(this.getSelectableQuadtree(), radius)
      .forEach((e) => {
        e.isInWarFog = false;
      })
      ;

  }

  tractResources(modifier: number): void {
    if (!this.tractorRange)
      return;

    this.getSelectableQuadtree()
      ?.filter((item: QuadtreeItem) => {
        const itemFaction = this.getScene().elements[item.id]?.getFaction();
        return itemFaction === Faction.RESOURCE;
      })
      ?.colliding({
        x: this.x - this.tractorRange,
        y: this.y - this.tractorRange,
        width: this.tractorRange * 2,
        height: this.tractorRange * 2,
      })
      ?.map((e: QuadtreeItem) => this.getScene().elements[e.id])
      ?.filter((element): element is Resource => {
        return element instanceof Resource;
      })
      ?.forEach((r: Resource) => {
        r.moveTowardsShip(modifier, this);
      });
  }

  drawLifeBar(): void {
    const selected = this.isSelected();
    const hovered = this.isHovered();
    const fullLife = (this.HP === this.HPMax);
    const fullShield = (!this.shield || (this.shield && this.shield.HP === this.shield.HPMax));
    if (fullLife && fullShield && (!selected && !hovered) && this.faction !== Faction.ENEMY)
      return;

    let startColor;
    switch (this.faction) {
      case Faction.ENEMY:
        startColor = { R: 255, G: 0, B: 0, A: 1 };
        break;
      case Faction.HAZARD:
        startColor = { R: 255, G: 150, B: 0, A: 1 };
        break;
    }

    drawLifeBar({
      x: this.x - this.width / 2,
      y: this.y - this.height / 2 - this.width / 10,
      width: this.width,
      height: this.width / 10,
      ratio: this.HP / this.HPMax,
      context: this.context,
      startColor,
    });

    if (this.generator) {
      this.generator.drawEnergyBar();
    }
  }

  get controlGroupNumber(): string | undefined {
    const controlGroups = Game.instance.controlSchemes?.rts.controlGroups;
    return Object.keys(controlGroups)
      .find((groupNumber) => {
        return controlGroups[parseInt(groupNumber)]?.elementsIds?.includes(this.id);
      })
      ;
  }

  drawControlGroupNumber(): void {
    if (!this.isSelected())
      return;

    const { controlGroupNumber } = this;
    if (controlGroupNumber) {
      const { context: ctx } = this;
      ctx.save();
      ctx.font = '20px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'white';
      ctx.fillText(controlGroupNumber, this.x, this.y - this.height);
      ctx.restore();
    }
    return;
  }

  accelerate(modifier: number): void {
    this.moveSpeed = Math.min(
      this.moveSpeed + this.accelerationSpeed * modifier,
      this.moveSpeedMax,
    );
    [...this.reactors]
      .forEach(reactor => reactor.update(modifier))
      ;
  }

  decelerate(modifier: number): void {
    this.moveSpeed = Math.max(
      this.moveSpeed - this.decelerationSpeed * modifier,
      0,
    );
  }

  brake(modifier: number): void {
    this.moveSpeed = Math.max(
      this.moveSpeed - this.brakeSpeed * modifier,
      0,
    );
  }

  get isStrafing(): boolean {
    return this?.behaviour?.orders?.[0] instanceof StrafeToOrder;
  }

  strafe(modifier: number, angle: number): void {
    if (!this.canStrafe)
      return;
    this.moveByDistanceAndAngle(this.strafeSpeed * modifier, angle);
  }

  strafeLeft(modifier: number): void {
    return this.strafe(modifier, getCorrectAngle(this.lookAngle + 90));
  }

  strafeRight(modifier: number): void {
    return this.strafe(modifier, getCorrectAngle(this.lookAngle - 90));
  }

  strafeDown(modifier: number): void {
    return this.strafe(modifier, getCorrectAngle(this.lookAngle + 180));
  }

  /* FTL jumps */

  toggleJump(): void {
    if (!this.canJump)
      return;
    this.jumpCharging = !this.jumpCharging;
  }

  chargeJump(modifier: number, reversed: boolean): void {
    if (!this.canJump) return;

    this.jumpCharge = Math.min(
      this.jumpChargeMax,
      Math.max(
        this.jumpCharge + this.jumpChargePerSecond * modifier * (reversed ? -4 : 1),
        0
      )
    );

    if (!this.jumpCharging) return;

    /* Cool particle effect during charging: particle are emitted more and more frequently as the charge get near to completion */
    const jumpChargeRatio = this.jumpCharge / this.jumpChargeMax;
    if (
      Math.random() >= (1 - jumpChargeRatio)
      && jumpChargeRatio < 0.95 // no more particle emission at the very end of charging sequence
    ) {
      const particleStartCoords = getXYByDistanceAndAngle({
        x: this.x,
        y: this.y,
        angle: Math.random() * 360,
        distance: this.sprite.height / 1.5
      });
      const particleEndCoords = {
        x: this.x,
        y: this.y,
      };
      new Particle({
        scene: this.scene,
        context: this.context,
        ...particleStartCoords,
        width: 30 * jumpChargeRatio,
        endWidth: 1,
        range: getDistanceBy2XY({
          x1: particleStartCoords.x,
          y1: particleStartCoords.y,
          x2: particleEndCoords.x,
          y2: particleEndCoords.y,
        }),
        moveAngle: getAngleBy2XY({
          x1: particleStartCoords.x,
          y1: particleStartCoords.y,
          x2: particleEndCoords.x,
          y2: particleEndCoords.y,
        }),
        moveSpeed: 1500 * jumpChargeRatio,
        startColor: { R: 255, G: 255, B: 255, A: 1 },
        endColor: { R: 255, G: 255, B: 255, A: 0.25 },
      }).create();
    }

    if (this.jumpCharge === this.jumpChargeMax) {
      this.jumpCharge = 0;
      this.jumpCharging = false;
      this.jumpOut();
    }
  }

  jumpIn(): void {
    const ship = this;
    const animationTravel = 1500;
    const ftlAnimation = new Animation({
      reverse: true,
      scene: this.scene,
      context: Game.instance.contexts.foregroundCanvas,
      x: getXByDistanceAndAngle({ x: this.x, angle: getReverseAngle(this.lookAngle), distance: animationTravel }),
      y: getYByDistanceAndAngle({ y: this.y, angle: getReverseAngle(this.lookAngle), distance: animationTravel }),
      width: this.width * 6,
      height: this.height * 6,
      lookAngle: this.lookAngle,
      moveAngle: this.lookAngle,
      endCoords: {
        x: this.x,
        y: this.y,
      },
      spriteParams: {
        ...spritesConfig.animations.ftl,
      },
      onEnd: () => {
        ship.create();

        /* Destroy everything near the jumping in ship */
        const aoeRange = 500;
        const elementsInRange = this.getElementsInRange(ship.getTargetsQuadtree({ includeHazards: true }), aoeRange);
        elementsInRange.forEach(element => {
          if (element.getFaction() === Faction.PLAYER) return; // don't affect player's units
          if (element instanceof Ship) {
            element.destroy();
          } else {
            element.remove();
          }
        });
      }
    });
    ftlAnimation.create();
  }

  jumpOut(): void {
    const animationTravel = 1500;
    new Animation({
      scene: this.scene,
      context: this.context,
      x: this.x,
      y: this.y,
      width: this.width * 6,
      height: this.height * 6,
      lookAngle: this.lookAngle,
      moveAngle: this.lookAngle,
      endCoords: getXYByDistanceAndAngle({ x: this.x, y: this.y, angle: this.lookAngle, distance: animationTravel }),
      spriteParams: {
        ...spritesConfig.animations.ftl,
      },
    }).create();

    this.behaviour?.clearOrders();
    this.moveSpeed = 0;
    this.remove();
    Game.instance.player.jumpedOutShips.push(this);

    /* Destroy everything in front of the jumping out ship */
    const aoeRange = 1000;
    const elementsInRange = this.getElementsInRange(this.getTargetsQuadtree({ includeHazards: true }), aoeRange);
    elementsInRange.forEach(element => {
      const angleToElement = getAngleBy2XY({
        x1: this.x,
        y1: this.y,
        x2: element.x,
        y2: element.y,
      });
      const angleDiff = Math.abs(getDiffBetween2Angles(this.lookAngle, angleToElement));

      if (angleDiff > 45) return; // not in front 
      if (element.getFaction() === Faction.PLAYER) return; // don't affect player's units

      if (element instanceof Ship) {
        element.destroy();
      } else {
        element.remove();
      }
    });
  }

  drawJumpChargeBar(): void {
    if (this.jumpCharge <= 0)
      return;
    drawLifeBar({
      width: this.width,
      height: this.width / 10,
      x: this.x - this.width / 2,
      y: this.y + this.height / 2,
      startColor: { R: 255, G: 255, B: 0, A: 1 },
      endColor: { R: 255, G: 255, B: 0, A: 1 },
      ratio: this.jumpCharge / this.jumpChargeMax,
      context: this.context,
    });
  }
}
